import {
  Alert,
  AlertCardType,
} from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import {
  ChurnRiskHubCardData,
  HubCardType,
  LostItemHubCardData,
  OverduePaymentHubCardData,
} from '@/types';
import { getTranslationValue } from '@/utils/translations';

type RawAlert =
  | (OverduePaymentHubCardData & { card_type: HubCardType.OverduePayment })
  | (LostItemHubCardData & { card_type: HubCardType.LostItem })
  | (ChurnRiskHubCardData & { card_type: HubCardType.ChurnRisk });

const ALERT_CARD_TYPES = new Set<HubCardType>([
  HubCardType.OverduePayment,
  HubCardType.LostItem,
  HubCardType.ChurnRisk,
]);

const toAlertCard = (raw: RawAlert): Alert | null => {
  // Defensive: the feed only renders the alert card types below. Non-alert hub
  // cards can omit the shared fields, so drop them before resolving `base`.
  if (!ALERT_CARD_TYPES.has(raw.card_type)) {
    return null;
  }

  const base = {
    alertUUID: raw.alert_uuid,
    chatUUID: raw.chat_uuid,
    description: raw.description
      ? getTranslationValue(raw.description)
      : undefined,
    restaurantUUID: raw.restaurant_uuid,
    status: raw.alert_status,
    subtitle: getTranslationValue(raw.subtitle),
    title: getTranslationValue(raw.title),
  };

  switch (raw.card_type) {
    case HubCardType.OverduePayment:
      return {
        ...base,
        amount: raw.amount,
        cardType: HubCardType.OverduePayment,
        currencyCode: raw.currency_code,
        invoiceNumber: raw.invoice_number,
        orderInvoiceUUID: raw.order_invoice_uuid,
        overdueByText: getTranslationValue(raw.overdue_by_text),
      };
    case HubCardType.LostItem:
      return {
        ...base,
        cardType: HubCardType.LostItem,
        displayName: raw.display_name,
        externalItemId: raw.external_item_id,
        packUnit: raw.pack_unit,
        photoUrls: raw.photo_url_list ?? [],
        variantPackUUID: raw.variant_pack_uuid,
      };
    case HubCardType.ChurnRisk:
      return { ...base, cardType: HubCardType.ChurnRisk };
    default:
      return null;
  }
};

/**
 * Parses the `card_data_json` payload returned by `getAllHubAlerts` into the
 * domain {@link Alert} view model, resolving translations to the active locale
 * and dropping any card types the alert feed does not render.
 *
 * @param cardDataJson - Stringified `HubCardData` entries from `getAllHubAlerts`.
 * @returns The renderable {@link Alert} view models, in payload order.
 */
export const transformAlertCards = (cardDataJson: string[]): Alert[] =>
  cardDataJson.flatMap((json) => {
    const alert = toAlertCard(JSON.parse(json) as RawAlert);
    return alert ? [alert] : [];
  });

/** A feed section: every alert of one {@link AlertCardType}. */
export interface AlertCategory {
  alerts: Alert[];
  cardType: AlertCardType;
}

const CATEGORY_ORDER: AlertCardType[] = [
  HubCardType.LostItem,
  HubCardType.OverduePayment,
  HubCardType.ChurnRisk,
];

const categoryRank = (cardType: AlertCardType): number => {
  const index = CATEGORY_ORDER.indexOf(cardType);
  return index === -1 ? CATEGORY_ORDER.length : index;
};

export const toAlertCategories = (alerts: Alert[]): AlertCategory[] => {
  const alertsByType = new Map<AlertCardType, Alert[]>();
  alerts.forEach((alert) => {
    const group = alertsByType.get(alert.cardType);
    if (group) {
      group.push(alert);
    } else {
      alertsByType.set(alert.cardType, [alert]);
    }
  });
  return Array.from(alertsByType, ([cardType, categoryAlerts]) => ({
    alerts: categoryAlerts,
    cardType,
  })).sort((a, b) => categoryRank(a.cardType) - categoryRank(b.cardType));
};
